import { LitElement, html, css, type PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

/** <cadence-avatar name="Alice Murphy" src="..." size="36"> */
@customElement('cadence-avatar')
export class CadenceAvatar extends LitElement {
  @property() name = '';
  @property() src = '';
  @property({ type: Number }) size = 36;
  @state() private failed = false;

  static styles = css`
    :host { display: inline-flex; vertical-align: middle; flex: none; }
    .av {
      display: grid; place-items: center; border-radius: 50%; overflow: hidden;
      background: var(--color-primary); color: #fff; font-weight: 700;
      flex: none; box-sizing: border-box; line-height: 1;
    }
    img { display: block; width: 100%; height: 100%; object-fit: cover; }
  `;

  protected willUpdate(changed: PropertyValues): void {
    // A new image should get a fresh chance to load (clear a prior error).
    if (changed.has('src')) this.failed = false;
  }

  private initials(): string {
    const parts = this.name.trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '?';
    return ((parts[0][0] ?? '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
  }

  render() {
    const dim = `${this.size}px`;
    const fontSize = `${Math.round(this.size * 0.4)}px`;
    // Hard-pin the box so a broken or oddly-sized image can never grow the avatar;
    // fall back to initials when there's no src or the image fails to load.
    const showImg = this.src && !this.failed;
    return html`
      <div
        class="av"
        style="width:${dim};height:${dim};min-width:${dim};min-height:${dim};font-size:${fontSize}"
      >
        ${showImg
          ? html`<img src=${this.src} alt=${this.name} @error=${() => (this.failed = true)} />`
          : this.initials()}
      </div>
    `;
  }
}
